import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import useHoverPeek from "@/hooks/useHoverPeek";
import SendtooLogo from "@/components/shared/SendtooLogo";
import {
  LayoutDashboard, Package, Wallet, User, LogOut, Menu, X,
  Users, Settings, BarChart3, MapPin, ChevronRight, Truck, Warehouse,
  Scale, FileText, Layers, CreditCard, ChevronDown,
  TrendingUp, Upload, Plus, RefreshCw,
  AlertTriangle, MessageCircle, LifeBuoy, Clock, ShieldCheck, Archive, Target, ChevronsLeft, ChevronsRight
} from "lucide-react";

const customerNav = [
  { to: "/customer", label: "Dashboard", icon: LayoutDashboard },
  { to: "/customer/shipments", label: "My Shipments", icon: Package },
  { to: "/customer/book", label: "Book Shipment", icon: Truck },
  { to: "/customer/pickup-requests", label: "Pickup Requests", icon: Truck },
  { to: "/customer/wallet", label: "Wallet", icon: Wallet },
  { to: "/customer/invoices", label: "Invoices", icon: FileText },
  { to: "/customer/payments", label: "Pay Dues", icon: CreditCard },
  { to: "/customer/rate-calculator", label: "Rate Calculator", icon: BarChart3 },
  { to: "/customer/disputes", label: "My Disputes", icon: AlertTriangle },
  { to: "/customer/support", label: "Support Tickets", icon: LifeBuoy },
  { to: "/customer/insurance-claims", label: "Insurance Claims", icon: ShieldCheck },
  { to: "/customer/profile", label: "Profile", icon: User },
];

// Staff: the same groups as admin, without Finance and Settings (pages and addresses unchanged).
const agentNav = [
  { to: "/staff/leads", label: "Leads", icon: Target },
  { to: "/staff", label: "Dashboard", icon: LayoutDashboard },
  {
    label: "Orders & Pickups",
    icon: Package,
    children: [
      { to: "/staff/book", label: "Create Forward Order", icon: Plus },
      { to: "/staff/orders", label: "My Orders", icon: Package },
      { to: "/staff/pickup-console", label: "Pickup Requests", icon: MapPin },
      { to: "/staff/bulk-upload", label: "Bulk Upload", icon: Upload },
      { to: "/staff/offline-bookings", label: "Offline Bookings", icon: FileText },
    ],
  },
  {
    label: "Operations",
    icon: Layers,
    children: [
      { to: "/staff/assignment", label: "Assignment Console", icon: Layers },
      { to: "/staff/network-assignments", label: "By Network", icon: Layers },
      { to: "/staff/ndr", label: "Delivery Exceptions (NDR)", icon: AlertTriangle },
      { to: "/admin/enterprise", label: "Enterprise Accounts", icon: Warehouse },
      { to: "/admin/enterprise-routes", label: "Routing Lanes", icon: MapPin },
    ],
  },
  {
    label: "Customers",
    icon: Users,
    children: [
      { to: "/staff/customers", label: "Customers", icon: Users },
      { to: "/staff/customer-approvals", label: "Approvals", icon: Users },
    ],
  },
  {
    label: "Billing",
    icon: CreditCard,
    children: [
      { to: "/staff/revenue", label: "Revenue", icon: BarChart3 },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/payment-proofs", label: "Dues & Payment Proofs", icon: CreditCard },
      { to: "/staff/rate-calculator", label: "Rate Calculator", icon: BarChart3 },
    ],
  },
  { to: "/staff/support", label: "Support Tickets", icon: LifeBuoy },
];

// Admin: 8 groups (Delhivery-style menu and sub-menu). Every page keeps its address.
const adminNav = [
  { to: "/admin/leads", label: "Leads", icon: Target },
  {
    label: "Home",
    icon: LayoutDashboard,
    children: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/ops-overview", label: "Ops Overview", icon: LayoutDashboard },
    ],
  },
  {
    label: "Orders & Pickups",
    icon: Package,
    children: [
      { to: "/admin/book", label: "Create Forward Order", icon: Plus },
      { to: "/admin/shipments", label: "Forward Orders", icon: Package },
      { to: "/staff/pickup-console", label: "Pickup Requests", icon: MapPin },
      { to: "/admin/bulk-upload", label: "Bulk Upload", icon: Upload },
      { to: "/staff/offline-bookings", label: "Offline Bookings", icon: FileText },
      { to: "/admin/old-shipments", label: "Old Shipments", icon: Archive },
      { to: "/admin/old-pickup-requests", label: "Old Pickup Requests", icon: Archive },
    ],
  },
  {
    label: "Operations",
    icon: Layers,
    children: [
      { to: "/admin/assignment", label: "Assignment Console", icon: Layers },
      { to: "/admin/network-assignments", label: "By Network", icon: Layers },
      { to: "/admin/ndr", label: "Delivery Exceptions (NDR)", icon: AlertTriangle },
      { to: "/admin/demurrage", label: "Demurrage Queue", icon: Clock },
    ],
  },
  {
    label: "Enterprise",
    icon: Warehouse,
    children: [
      { to: "/admin/enterprise", label: "Accounts & SKUs", icon: Users },
      { to: "/admin/enterprise-routes", label: "Routing Lanes", icon: MapPin },
    ],
  },
  {
    label: "Finance",
    icon: CreditCard,
    children: [
      { to: "/admin/revenue", label: "Revenue", icon: BarChart3 },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/payment-proofs", label: "Dues & Payment Proofs", icon: CreditCard },
      { to: "/admin/wallet-dashboard", label: "Wallet", icon: Wallet },
      { to: "/admin/wallet-ledger-v2", label: "Wallet Ledger", icon: Wallet },
      { to: "/admin/reconciliation-v2", label: "Reconciliation", icon: Scale },
      { to: "/admin/month-end", label: "Month-end Check", icon: FileText },
      { to: "/admin/gst-report", label: "GST Report", icon: FileText },
      { to: "/admin/quotations", label: "Quotations", icon: FileText },
      { to: "/admin/rate-agreements", label: "Rate Agreements", icon: FileText },
    ],
  },
  {
    label: "Customers & Partners",
    icon: Users,
    children: [
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/customer-approvals", label: "Approvals", icon: Users },
      { to: "/admin/partners", label: "Partners", icon: Truck },
      { to: "/admin/staff", label: "Staff", icon: ShieldCheck },
    ],
  },
  {
    label: "Support & Claims",
    icon: LifeBuoy,
    children: [
      { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy },
      { to: "/admin/disputes", label: "Disputes", icon: MessageCircle },
      { to: "/admin/insurance-claims", label: "Insurance Claims", icon: ShieldCheck },
    ],
  },
  {
    label: "Reports & Settings",
    icon: Settings,
    children: [
      { to: "/admin/reports", label: "Reports", icon: BarChart3 },
      { to: "/admin/reconciliation", label: "Reconciliation (old)", icon: Scale },
      { to: "/admin/rate-engine", label: "Rate Engine", icon: Settings },
      { to: "/admin/config", label: "Admin Config", icon: Settings },
      { to: "/admin/oda-pincodes", label: "ODA Pincodes", icon: MapPin },
      { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse },
      { to: "/admin/working-calendar", label: "Working Calendar", icon: Clock },
      { to: "/admin/transporter-ids", label: "Transporter IDs", icon: Truck },
      { to: "/admin/security", label: "Security (2FA)", icon: ShieldCheck },
    ],
  },
];

function getNavLinks(role) {
  if (role === "admin") return adminNav;
  if (role === "agent" || role === "staff") return agentNav;
  return customerNav;
}

function getPartnerNav(partnerType) {
  const t = (partnerType || "NETWORK").toUpperCase();
  const items = [
    { to: "/partner", label: "Dashboard", icon: LayoutDashboard },
  ];
  if (t === "NETWORK" || t === "REVENUE") {
    items.push({ to: "/partner/bookings", label: "New Booking", icon: Plus });
  }
  items.push({ to: "/partner/shipments", label: "My Shipments", icon: Package });
  items.push({ to: "/partner/earnings", label: "My Earnings", icon: TrendingUp });
  if (t === "NETWORK" || t === "EXECUTION") {
    items.push({ to: "/partner/upload-proof", label: "Upload Proof", icon: Upload });
  }
  return items;
}

function NavGroup({ group, currentPath, onNavigate, collapsed, onExpand }) {
  const childPaths = group.children.map((c) => c.to);
  // a portal root such as "/admin" is active only on itself, not on every "/admin/..." page
  const isRoot = (p) => p.split("/").filter(Boolean).length < 2;
  const matches = (p) => currentPath === p || (!isRoot(p) && currentPath.startsWith(p + "/"));
  const isChildActive = childPaths.some(matches);
  const [open, setOpen] = useState(isChildActive);
  const GroupIcon = group.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => { if (collapsed) { onExpand(); setOpen(true); } else setOpen((v) => !v); }}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
        title={collapsed ? group.label : undefined}
        aria-label={collapsed ? group.label : undefined}
        className={`w-full flex items-center gap-3 rounded-md text-sm font-medium transition-all min-h-[44px] ${collapsed ? "justify-center px-0" : "px-3 py-2.5"} ${
          isChildActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`}
      >
        <span className="relative flex-shrink-0">
          <GroupIcon className="w-5 h-5" strokeWidth={1.5} />
          {collapsed && isChildActive && <span className="absolute -right-1 -bottom-1 w-2 h-2 rounded-full bg-red-500" />}
        </span>
        {!collapsed && <span className="flex-1 text-left">{group.label}</span>}
        {!collapsed && <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && !collapsed && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
          {group.children.map((c) => {
            const active = matches(c.to);
            const ChildIcon = c.icon;
            return (
              <Link
                key={`${c.to}-${c.label}`}
                to={c.to}
                data-testid={`nav-${c.label.toLowerCase().replace(/ /g, "-")}`}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all min-h-[40px] ${
                  active ? "bg-white/15 text-white" : "text-slate-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <ChildIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1">{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout({ children, portalColor }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop sidebar can collapse to icons only (Delhivery-style); remembered per browser.
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("sendtoo_sidebar_collapsed") === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem("sendtoo_sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* private mode */ } }, [collapsed]);
  // While collapsed, hovering the ribbon (or its arrow) slides the full menu out; leaving slides it back.
  const { peek, close: closePeek, bind: peekBind } = useHoverPeek(collapsed);
  const wide = !collapsed || peek;

  const navLinks = user?.role === "partner"
    ? getPartnerNav(user?.partner_type)
    : getNavLinks(user?.role);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const roleLabel = { customer: "Customer Portal", agent: "Staff Portal", staff: "Staff Portal", admin: "Admin Panel", partner: "Partner Portal" }[user?.role] || "Portal";
  const roleColor = portalColor === "green"
    ? "bg-emerald-600"
    : { customer: "bg-blue-600", agent: "bg-orange-500", staff: "bg-orange-500", admin: "bg-purple-700", partner: "bg-emerald-600" }[user?.role] || "bg-brand-500";

  const SidebarContent = ({ mini = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 border-b border-slate-700 ${mini ? "justify-center px-2 py-4" : "px-6 py-5"}`}>
        {mini ? <img src="/icons/icon-192.png" alt="Sendtoo" className="w-9 h-9 rounded-lg" />
          : <img src="/assets/sendtoo-logo-white.png" alt="Sendtoo" className="w-full max-w-[180px] h-auto" />}
      </div>

      {/* Role badge */}
      {!mini && (
        <div className="px-6 py-3">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white ${roleColor}`}>
            {roleLabel}
          </span>
        </div>
      )}

      {/* Nav links */}
      <nav className={`flex-1 py-2 space-y-0.5 overflow-y-auto ${mini ? "px-2" : "px-3"}`}>
        {navLinks.map((link, idx) => {
          if (link.children) {
            return (
              <NavGroup
                key={`group-${link.label}-${idx}`}
                group={link}
                currentPath={location.pathname}
                onNavigate={() => setSidebarOpen(false)}
                collapsed={mini}
                onExpand={() => { closePeek(); setCollapsed(false); }}
              />
            );
          }
          const isActive = location.pathname === link.to || (link.to !== "/customer" && link.to !== "/agent" && link.to !== "/admin" && link.to !== "/partner" && location.pathname.startsWith(link.to));
          return (
            <Link
              key={`${link.to}-${link.label}`}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setSidebarOpen(false)}
              title={mini ? link.label : undefined}
              aria-label={mini ? link.label : undefined}
              className={`flex items-center gap-3 rounded-md text-sm font-medium transition-all min-h-[44px] ${mini ? "justify-center" : "px-3 py-2.5"} ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <link.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {!mini && <span className="flex-1">{link.label}</span>}
              {!mini && isActive && <ChevronRight className="w-4 h-4 opacity-70" />}
            </Link>
          );
        })}
      </nav>

      {/* User info + logout */}
      {mini ? (
        <div className="border-t border-slate-700 p-2 flex flex-col items-center gap-2">
          <div title={user?.name} className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">{user?.name?.[0]?.toUpperCase() || "U"}</div>
          <button data-testid="logout-btn" onClick={handleLogout} title="Logout" aria-label="Logout" className="p-2 text-slate-300 hover:text-white hover:bg-red-500/20 rounded-md"><LogOut className="w-4 h-4" /></button>
        </div>
      ) : (
      <div className="border-t border-slate-700 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        {(user?.role === "admin" || user?.role === "staff") && (
          <Link
            to="/account/change-password"
            data-testid="change-password-link"
            className="w-full flex items-center gap-2 px-3 py-2 mb-1 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-md transition-colors"
          >
            <FileText className="w-4 h-4" />
            Change password
          </Link>
        )}
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-red-500/20 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 font-body overflow-hidden">
      {/* Desktop Sidebar */}
      {/* The outer box holds the page's space; the inner panel glides wider over the page while hovered. */}
      <aside className={`hidden md:block relative z-40 flex-shrink-0 transition-[width] duration-300 ease-out ${collapsed ? "w-[68px]" : "w-60"}`} data-testid="desktop-sidebar" data-peek={peek ? "1" : "0"}>
        <div {...peekBind}
          className={`absolute inset-y-0 left-0 flex flex-col bg-[#0A183D] transition-[width,box-shadow] duration-300 ease-out ${wide ? "w-60" : "w-[68px]"} ${peek ? "shadow-2xl shadow-black/40" : ""}`}
          data-testid="sidebar-panel">
          <div className="flex-1 min-h-0 overflow-hidden flex"><div className={`flex flex-col shrink-0 ${wide ? "w-60" : "w-[68px]"}`}><SidebarContent mini={!wide} /></div></div>
          <button type="button" onClick={() => { closePeek(); setCollapsed((v) => !v); }} data-testid="sidebar-collapse"
            title={collapsed ? "Keep menu open" : "Collapse menu"} aria-label={collapsed ? "Keep menu open" : "Collapse menu"}
            className="absolute top-5 -right-3.5 z-20 w-7 h-7 rounded-md bg-black text-white border border-white/20 flex items-center justify-center shadow hover:bg-slate-800">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A183D] flex flex-col z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-4 flex-shrink-0">
          <button
            className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-800 hidden sm:block">{roleLabel}</h2>
          </div>
          {user?.role === "customer" && (
            <div className="flex items-center gap-1.5 bg-brand-50 text-brand-500 px-3 py-1.5 rounded-md text-sm font-semibold">
              <Wallet className="w-4 h-4" />
              ₹{(user?.wallet_balance || 0).toFixed(2)}
            </div>
          )}
          <Link to="/" className="md:hidden">
            <SendtooLogo size="md" />
          </Link>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
